import {
	init,
	type GenericAnalyticWebClientPromise,
} from '@atlaskit/react-ufo/interaction-metrics-init';
import type { Config } from '@atlaskit/react-ufo/config';
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import { initAnalyticsClient } from '@atlassian/twg-microsite-experience';
import { getConfig } from '@atlassian/twg-microsite-experience/config';

const kind = {
	page_load: 1,
	// Single-route marketing-style app today — no real client-side
	// transitions occur. Leaving this on would only record noise (or
	// surprise telemetry if a route is added without revisiting this).
	transition: 0,
	press: 1,
	typing: 0,
	legacy: 0,
	hover: 0,
	segment: 0,
};

const { env, region } = getConfig();

const config: Config = {
	product: 'teamworkGraph',
	region,
	capability: {
		// `feature_flag_access: 0` for now — wiring `addFeatureFlagAccessed`
		// into the feature-flag layer is a follow-up. Without that hook
		// `feature_flag_access: 1` would advertise a capability we don't
		// fulfil yet.
		feature_flag_access: 0,
		react_profiler: 1,
	},
	kind,
	vc: {
		enabled: true,
		// Default (100) is plenty for a marketing-style page; reduces VC
		// compute on every paint, which matters more on mobile / low-end
		// devices that often hit landing pages.
		heatmapSize: 100,
		oldDomUpdates: false,
		// Devtools surface should never ship to real users — gate to
		// non-prod to keep the prod bundle lean and avoid leaking
		// internal UFO state on `window`.
		devToolsEnabled: env !== 'production',
		selectorConfig: {
			id: false,
			role: true,
			className: true,
			testId: true,
		},
	},
};

/**
 * Boot React UFO for the TwG Microsite.
 *
 * Must be called *before* `createRoot(...).render(<App />)` in `index.tsx`.
 * Reuses the singleton `AnalyticsWebClient` returned by `initAnalyticsClient`
 * so UFO and product analytics share one transport.
 */
export function initUFO(): void {
	const client = initAnalyticsClient() as unknown as GenericAnalyticWebClientPromise;
	init(Promise.resolve(client), config);

	// Single-route app today — fire the page-load trace immediately with the
	// known route name so the initial interaction is fully classified.
	// Both args use the route registry name `HOME` (see ./ui/routes/index.ts)
	// so the interaction name and route name are consistent in dashboards.
	traceUFOPageLoad('HOME', 'HOME');
}
